
import React from 'react';
import { MolecularBackground } from './molecular-background';
import { HeartbeatAnimation } from './heartbeat-animation';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useIsMobile } from '@/hooks/use-mobile';

interface PageBackgroundProps {
  variant?: 'default' | 'ai' | 'pregnancy' | 'professional';
  intensity?: 'low' | 'medium' | 'high';
  className?: string;
  animate?: boolean;
}

export const PageBackground: React.FC<PageBackgroundProps> = ({
  variant = 'default',
  intensity = 'medium',
  className = '',
  animate = true
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isMobile = useIsMobile();
  
  // Intensity multipliers
  const intensityMap = {
    low: { molecules: 8, pulses: 1, opacity: 'opacity-10' },
    medium: { molecules: 15, pulses: 2, opacity: 'opacity-20' },
    high: { molecules: 25, pulses: 3, opacity: 'opacity-30' }
  };
  
  // Variant-specific classes
  const variantClasses = {
    default: 'bg-gradient-to-br from-[#E8F0FF] via-[#F0F7FF] to-[#F8FBFF]',
    ai: 'bg-gradient-to-r from-[#F0F8FF] to-[#E0F0FF]',
    pregnancy: 'bg-gradient-to-r from-[#FFF0F5] to-[#FFFACD]',
    professional: 'bg-gradient-to-r from-[#f0f8ff] to-[#e0f0ff]'
  };

  return (
    <div className={`fixed inset-0 -z-10 ${variantClasses[variant]} ${className}`}>
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white/30 to-blue-50/50"></div>
      
      {/* Grid pattern */}
      <div className="absolute inset-0 grid-texture opacity-5"></div>
      
      {/* Hexagon pattern */}
      <div className="absolute inset-0 hexagon-network"></div>
      
      {/* Connection lines and nodes */}
      <div className="absolute inset-0">
        <div className="connection-network"></div>
      </div>
      
      {/* Decorative elements */}
      {animate && (
        <>
          <MolecularBackground 
            count={intensityMap[intensity].molecules}
            className={intensityMap[intensity].opacity} 
          />
          <HeartbeatAnimation 
            count={intensityMap[intensity].pulses}
            className={intensityMap[intensity].opacity} 
          />
        </>
      )}
    </div>
  );
};
